import { useMemo } from "react"
import { useNavigate } from "react-router-dom"
import type { InlineAction } from "@/components/mobile/MobileTopNav"
import { useToggleCommunitySave } from "@/features/profile/hooks/useSavedCommunities"
import { useToggleRitualSave } from "@/features/profile/hooks/useSavedRituals"
import { useUIStore } from "@/store/uiStore"

interface InlineActionsParams {
  isMainNav: boolean
  isSettingsRoute: boolean
  isEditProfilePage: boolean
  isRitualDetail: boolean
  isCommunityDetail: boolean
  isProfilePage: boolean
  isOtherProfilePage: boolean
  ritualId: string | undefined
  ritualUIData: { isSaved?: boolean; canEdit?: boolean } | undefined
  communityId: string | undefined
  communityData: { isSaved?: boolean } | undefined
  setShareOpen?: (open: boolean) => void
}

interface InlineActionsResult {
  inlineActions: InlineAction[] | undefined
}

export function useInlineActions({
  isMainNav,
  isSettingsRoute,
  isEditProfilePage,
  isRitualDetail,
  isCommunityDetail,
  isProfilePage,
  isOtherProfilePage,
  ritualId,
  ritualUIData,
  communityId,
  communityData,
  setShareOpen,
}: InlineActionsParams): InlineActionsResult {
  const navigate = useNavigate()
  const { setShowEditProfileTipsModal } = useUIStore()
  const toggleRitualSave = useToggleRitualSave()
  const toggleCommunitySave = useToggleCommunitySave()

  const inlineActions = useMemo<InlineAction[] | undefined>(() => {
    if (isMainNav || isSettingsRoute) return undefined
    const actions: InlineAction[] = []

    if (isEditProfilePage) {
      actions.push({
        icon: "info",
        onClick: () => setShowEditProfileTipsModal(true),
      })
    } else if (isRitualDetail || isCommunityDetail || isProfilePage || isOtherProfilePage) {
      if (isRitualDetail && ritualId) {
        actions.push({
          icon: "save",
          onClick: () => toggleRitualSave.mutate(ritualId),
          active: ritualUIData?.isSaved ?? false,
        })
      }
      actions.push({
        icon: "share",
        onClick: () => setShareOpen?.(true),
      })
      if (isRitualDetail && ritualId && ritualUIData?.canEdit) {
        actions.push({
          icon: "ritualEdit",
          onClick: () => navigate(`/rituals/edit/${ritualId}`),
        })
      }
    }

    if (isCommunityDetail && communityId) {
      actions.push({
        icon: "save",
        onClick: () => toggleCommunitySave.mutate(communityId),
        active: communityData?.isSaved ?? false,
      })
    }

    return actions.length > 0 ? actions : undefined
  }, [
    isMainNav,
    isSettingsRoute,
    isEditProfilePage,
    isRitualDetail,
    isCommunityDetail,
    isProfilePage,
    isOtherProfilePage,
    ritualId,
    ritualUIData,
    communityId,
    communityData,
    toggleRitualSave,
    toggleCommunitySave,
    setShowEditProfileTipsModal,
    navigate,
    setShareOpen,
  ])

  return { inlineActions }
}
